<template>
    <div class="relative pb-8">
        <div v-if="! embedded">
            <document-tabs
                current_tab="signatures"
                :back_route="envelope ? `${documents_index_route}#signatures` : null"
                :documents_index_route="documents_index_route"
                :signatures_index_route="`${documents_index_route}#signatures`"
            />
        </div>

        <p v-if="embedded" class="signature-introduction text-sm text-gray-600">To send documents for signatures, create an envelope and add your documents to it.</p>

        <div v-if="! creating && ! envelope && (!embedded || !has_active_docusign_user)" class="flex justify-start mb-3">
            <docusign-status
                :has_active_docusign_user="has_active_docusign_user"
                :docusign_user="docusign_user"
                :envelopes_available="envelopes_available"
                :compact="embedded"
            />
        </div>

        <!-- Button bar -->
        <div v-if="has_active_docusign_user && !creating && !envelope && (! embedded || ! envelopes_available)" class="w-full flex justify-end items-center space-x-4 bg-gray-100 px-4 py-4 mb-4 border border-gray-300">
            <button
                v-if="envelopes_available"
                type="button"
                class="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 h-10"
                @click="createEnvelope"
            >
                <div class="flex items-center">
                    <i class="far fa-envelope mr-2 text-sm"></i>

                    <div>
                        {{ $t('Create Envelope') }}
                    </div>
                </div>
            </button>

            <div v-else-if="has_active_docusign_user"
                class="flex flex-col items-end"
            >
                <p class="text-sm text-blue-600 font-semibold mb-2">
                    To create a new envelope, purchase additional Docusign credit.
                </p>

                <button-blue-outline @click="purchaseDocusignCredit">
                    Purchase Docusign Credit
                </button-blue-outline>
            </div>
        </div>

        <div v-if="creating || envelope"
            class="mt-6 pb-6"
        >
            <envelope-detail
                :read_only="read_only"
                :envelope="envelope"
                :property="property"
                :documents="documents"
                :templates="templates"
                :document_folders="document_folders"
                :envelope_count="envelopes?.length || 0"
                :envelopes_available="envelopes_available"
                :signatures_view_docusign_route="signatures_view_docusign_route"
                :signatures_create_route="signatures_create_route"
                :signatures_index_route="signatures_index_route"
                :signatures_destroy_route="signatures_destroy_route"
                :signature_purchase_route="signature_purchase_route"
                :prompt_for_broker_completion="prompt_for_broker_completion"
                @cancel="cancelCreateEnvelope"
            />
        </div>

        <div v-else-if="docusign_user && docusign_user.error">
            <p class="text-md text-red-600 mt-8">
                There was an error activating your Docusign account. <br>
                Please contact Support for assistance.
            </p>
        </div>

        <div v-else-if="! docusign_user">
            <p class="text-md text-blue-600 mt-8">
                Docusign access is being activated on your account, and will be available shortly.
            </p>
        </div>

        <div v-else-if="has_active_docusign_user" class="signature-envelope-panel">
            <div v-if="embedded" class="signature-envelope-toolbar">
                <docusign-status :has_active_docusign_user="has_active_docusign_user" :docusign_user="docusign_user" :envelopes_available="envelopes_available" :compact="true" />
                <button v-if="envelopes_available && !read_only" type="button" class="signature-create-button" @click="createEnvelope">
                    <mail-icon size="16" aria-hidden="true" />{{ $t('Create Envelope') }}
                </button>
            </div>
            <p v-if="! envelopes.length"
                class="text-sm text-gray-500 px-4 py-4"
            >
                To request signatures using Docusign, add one or more documents to an Envelope.
            </p>

            <envelope-list
                :property="property"
                :envelopes="envelopes"
            />
        </div>
        <modal :name="titleModalName" :adaptive="true" :focusTrap="true" :width="480" height="auto" :clickToClose="!savingTitle" @before-close="beforeTitleClose" @opened="focusTitle">
            <form class="p-6 bg-white" @submit.prevent="saveEnvelopeTitle">
                <h2 class="text-lg font-semibold text-gray-900 pb-4 mb-4">Create envelope</h2>
                <label class="block text-sm">
                    Title
                    <input ref="envelopeTitle" v-model="newTitle" required maxlength="255" :disabled="savingTitle" class="block w-full border border-gray-300 rounded px-3 py-2 mt-2 mb-4" />
                </label>
                <p v-if="titleError" role="alert" class="text-sm text-red-700 mb-4">{{ titleError }}</p>
                <div class="flex justify-end gap-3">
                    <button type="button" class="signature-modal-button" :disabled="savingTitle" @click="$modal.hide(titleModalName)">Cancel</button>
                    <button type="submit" class="signature-modal-button signature-modal-primary" :disabled="savingTitle || !newTitle.trim()">{{ savingTitle ? 'Saving…' : 'Save' }}</button>
                </div>
            </form>
        </modal>
    </div>
</template>

<script>
import { MailIcon } from 'vue-feather-icons';
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import DocusignStatus from './docusign-status.vue';
import DocumentTabs from '../document-tabs.vue';
import EnvelopeDetail from './envelope-detail.vue';
import EnvelopeList from './envelope-list.vue';

export default {
    components: {
        MailIcon,
        ButtonBlueOutline,
        DocusignStatus,
        DocumentTabs,
        EnvelopeDetail,
        EnvelopeList,
    },

    props: {
        property: {},
        documents: {},
        templates: {},
        document_folders: {},
        envelopes: {},
        envelope: {},
        index_route: {},
        read_only: { default: false },
        has_active_docusign_user: { default: false },
        docusign_user: {},
        envelopes_available: {},
        docusign_event: {},
        store_route: {},
        documents_index_route: {},
        signature_purchase_route: {},
        signatures_index_route: {},
        signatures_create_route: {},
        signatures_destroy_route: {},
        signatures_view_docusign_route: {},
        prompt_for_broker_completion: {},
        embedded: { default: false },
        start_creating: { default: false },
    },

    data() {
        return {
            newTitle: '', titleError: '', savingTitle: false,
            creating: this.start_creating,
            show_broker_completion_modal: false,
        }
    },

    computed: {
        titleModalName() { return `envelope-title-${this._uid}`; },
    },
    methods: {
        createEnvelope() {
            if (this.read_only || this.savingTitle) return;
            this.newTitle = ''; this.titleError = '';
            this.$modal.show(this.titleModalName);
        },
        focusTitle() { this.$refs.envelopeTitle?.focus(); },
        beforeTitleClose(event) { if (this.savingTitle) event.cancel(); },
        async saveEnvelopeTitle() {
            if (this.savingTitle || this.read_only) return;
            const title = this.newTitle.trim();
            if (!title) { this.titleError = 'Enter a title for this envelope.'; return; }
            this.savingTitle = true; this.titleError = '';
            try {
                const response = await axios.post(this.signatures_create_route, {
                    title, documents: [], recipients: [], broker_will_sign: !!this.property.broker_id,
                });
                window.location.href = response.data.show_route;
            } catch (error) {
                this.titleError = Object.values(error.response?.data?.errors || {}).flat()[0]
                    || error.response?.data?.error || error.response?.data?.message || 'The envelope could not be saved. Please try again.';
                this.savingTitle = false;
            }
        },
        cancelCreateEnvelope() {
            this.creating = false;
        },
        purchaseDocusignCredit() {
            window.location.href = this.signature_purchase_route;
        },
    },
}
</script>

<style scoped>
.signature-modal-button { border: 1px solid #d1d5db; border-radius: 0.375rem; padding: 0.55rem 0.9rem; font-size: 0.875rem; background: white; color: #374151; }
.signature-modal-primary { background: #2563eb; border-color: #2563eb; color: white; }
.signature-modal-button:disabled { opacity: 0.5; cursor: default; }
.signature-introduction { margin: 0 0 1rem; padding: 0.5rem 0; }
.signature-envelope-panel { border: 1px solid #d1d5db; border-radius: 0.5rem; overflow: hidden; background: #fff; }
.signature-envelope-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.5rem 0.75rem 0.5rem 1rem; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
.signature-create-button { display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; padding: 0.35rem 0.65rem; border: 1px solid #2563eb; border-radius: 0.375rem; background: #2563eb; color: white; font-size: 0.8125rem; white-space: nowrap; }
</style>
